import React, { useState } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faExclamationTriangle } from '@/icons';
import { Modal, ConfirmationModal } from './Modal';
import { useTheme, createThemedStyles } from '../contexts/ThemeContext';
import { useSimpleCaseContext } from '../contexts/SimpleCaseContext/SimpleCaseContextDef';

/**
 * SaveConflictModal -- shown when an autosave is rejected with 412 (another session saved this
 * case first). Replaces the old silent auto-recovery: the user must explicitly choose how to
 * proceed rather than having their save silently retried and potentially overwrite someone
 * else's changes with no warning (see docs/AUTOSAVE_DETAILS.md).
 */
export const SaveConflictModal: React.FC = () => {
    const theme = useTheme();
    const themedStyles = createThemedStyles(theme);
    const { saveConflictActive, resolveSaveConflict } = useSimpleCaseContext();
    const [confirmingForceSave, setConfirmingForceSave] = useState(false);

    if (!saveConflictActive) {
        return null;
    }

    return (
        <>
            <Modal
                isOpen={!confirmingForceSave}
                onClose={() => resolveSaveConflict('cancel')}
                title="Save Conflict"
                headerBackgroundColor={theme.colors.status.warning}
            >
                <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.md }}>
                    <div style={{
                        display: 'flex',
                        alignItems: 'flex-start',
                        gap: theme.spacing.md,
                        padding: theme.spacing.md,
                        backgroundColor: '#fef3c7',
                        borderRadius: theme.borderRadius.sm,
                        border: `1px solid ${theme.colors.status.warning || '#f59e0b'}`,
                    }}>
                        <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            style={{ fontSize: theme.typography.fontSize.lg, color: '#92400e', marginTop: theme.spacing.xs }}
                        />
                        <div style={{ flex: 1 }}>
                            <p style={{
                                ...themedStyles.text.body,
                                color: '#92400e',
                                fontWeight: theme.typography.fontWeight.medium,
                                margin: `0 0 ${theme.spacing.xs} 0`,
                            }}>
                                Another session saved changes to this case
                            </p>
                            <p style={{
                                ...themedStyles.text.body,
                                color: '#92400e',
                                fontSize: theme.typography.fontSize.sm,
                                margin: 0,
                            }}>
                                Your changes have not been saved yet. Choose how you'd like to proceed.
                            </p>
                        </div>
                    </div>

                    <div style={{ display: 'flex', flexDirection: 'column', gap: theme.spacing.sm }}>
                        {/* Make a Copy: hidden per product decision, plumbing (resolveSaveConflict('makeCopy')
                            in Provider.tsx) is unchanged and fully wired -- restore this button to re-enable.
                        <button
                            type="button"
                            onClick={() => resolveSaveConflict('makeCopy')}
                            style={themedStyles.button.secondary}
                        >
                            Make a Copy
                        </button>
                        */}
                        <button
                            type="button"
                            onClick={() => setConfirmingForceSave(true)}
                            style={{
                                ...themedStyles.button.secondary,
                                backgroundColor: '#dc2626',
                            }}
                        >
                            Force Save
                        </button>
                        <button
                            type="button"
                            onClick={() => resolveSaveConflict('cancel')}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                textAlign: 'center',
                                backgroundColor: 'transparent',
                                color: theme.colors.text.primary,
                                border: `1px solid ${theme.colors.border.default}`,
                                borderRadius: theme.borderRadius.lg,
                                padding: `${theme.spacing.sm} ${theme.spacing.lg}`,
                                fontSize: theme.typography.fontSize.md,
                                fontWeight: theme.typography.fontWeight.medium,
                                cursor: 'pointer',
                            }}
                        >
                            Cancel
                        </button>
                    </div>
                </div>
            </Modal>

            <ConfirmationModal
                isOpen={confirmingForceSave}
                onClose={() => setConfirmingForceSave(false)}
                onConfirm={() => {
                    setConfirmingForceSave(false);
                    resolveSaveConflict('forceSave');
                }}
                title="Overwrite the other session's changes?"
                message="This will completely overwrite the changes made by the other user, and their data may be permanently lost. This cannot be undone."
                confirmText="Force Save"
                cancelText="Cancel"
                confirmButtonStyle="danger"
            />
        </>
    );
};
